import { useEffect, useRef, useState } from 'react';
import type { BrandId, ReceiptChannel, ReceiptCurrency } from '@nadoutong/contracts';
import { RECEIPT_CHANNELS, RECEIPT_CURRENCIES } from '@nadoutong/contracts';
import {
  listReceiptAttachments,
  useBrandInvoiceSettings,
  useSubmitReceipt,
  useUploadReceiptAttachment,
} from '../api/receipts.ts';
import { useCustomerContact } from '../api/customerCrm.ts';
import { OpsApiError } from '../api/client.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import {
  RECEIPT_CHANNEL_LABEL_KEYS,
  attachmentMaxMbLabel,
  bytesToBase64,
  formatTaxPointBps,
  isOversizeAttachment,
  parseTaxPointInputToBps,
  randomClientRef,
} from '../lib/receipts.ts';
import { parseAmountInputToMinorUnits } from './formatters.ts';
import { LocalDateTimeInput } from './LocalDateTimeInput.tsx';
import { useToast } from './Toast.tsx';
import { Button, Checkbox, Dialog, FormField, Input, Select, Textarea } from '../ui/index.ts';

/**
 * Sales receipt entry (epic #1031 E2-R5, #1063): record ONE off-system
 * collection for a pinned customer. Reachable from a workspace row
 * action and the Customer 360 receipts tab — never from a free-floating
 * customer picker, so the org is always something the session already
 * proved it can see (ops-api re-proves ownership server-side and pins
 * `account_id` / `entered_by` / `brand` itself).
 *
 * The form only ships operator-collected facts: amount + currency,
 * channel, actual fee, received_at, optional contract ref / note, and
 * optional evidence (base64 JSON, ~5 MB cap checked here first for a
 * friendly error before the BFF re-enforces it).
 *
 * `client_ref` is generated once per dialog open — retrying after a
 * network error replays idempotently instead of double-submitting. If
 * the receipt lands but the evidence upload fails, the dialog stays
 * open in attachment-retry mode (the receipt is NOT resubmitted).
 */

interface ReceiptSubmitDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly orgId: string;
  readonly customerEmail: string;
  /**
   * The pinned customer's signup brand — selects which invoice fields
   * render (#1064): cn-tax brands collect a 票点 (%) + title + tax no;
   * commercial brands collect a title only. Display-side selection
   * only: ops-api/platform re-derive and re-validate the brand
   * server-side.
   */
  readonly customerBrand: BrandId;
  /** Called after a fully successful submit (receipt + optional evidence). */
  readonly onSubmitted?: () => void;
}

interface PendingUpload {
  readonly receiptId: string;
}

export function ReceiptSubmitDialog({
  open,
  onOpenChange,
  orgId,
  customerEmail,
  customerBrand,
  onSubmitted,
}: ReceiptSubmitDialogProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  // CRM contact card of the pinned customer — shows the company name
  // finance will reconcile this receipt against, and nudges sales to
  // complete the card when it is still missing. Fetched only while the
  // dialog is open (the '' orgId disables the query).
  const contactQuery = useCustomerContact(open ? orgId : '');
  const contactCompany = contactQuery.data?.contact?.company ?? null;

  const [clientRef, setClientRef] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<ReceiptCurrency>('CNY');
  const [channel, setChannel] = useState<ReceiptChannel>('bank');
  const [fee, setFee] = useState('');
  const [receivedAt, setReceivedAt] = useState('');
  const [contractRef, setContractRef] = useState('');
  const [note, setNote] = useState('');
  const [needInvoice, setNeedInvoice] = useState(false);
  const [taxPoint, setTaxPoint] = useState('');
  const [invoiceTitle, setInvoiceTitle] = useState('');
  const [taxNo, setTaxNo] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Set once the receipt row exists but its evidence upload failed —
  // from then on the primary action retries ONLY the upload.
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Synchronous re-entrancy guard for the upload path (#1098): the
  // react-query pending flag only flips after a re-render, so a rapid
  // double-click while the file bytes are being read could otherwise
  // start two uploads of the same evidence.
  const uploadInFlightRef = useRef(false);

  useEffect(() => {
    if (open) {
      setClientRef(randomClientRef());
      setReceivedAt(new Date().toISOString());
      return;
    }
    setAmount('');
    setCurrency('CNY');
    setChannel('bank');
    setFee('');
    setContractRef('');
    setNote('');
    setNeedInvoice(false);
    setTaxPoint('');
    setInvoiceTitle('');
    setTaxNo('');
    setFile(null);
    setValidationError(null);
    setPendingUpload(null);
  }, [open]);

  const submit = useSubmitReceipt();
  const upload = useUploadReceiptAttachment();
  const busy = submit.isPending || upload.isPending;

  // Per-brand invoice config (#1064): the pinned customer's brand mode
  // decides which invoice fields render. Fetched only while open.
  const invoiceSettings = useBrandInvoiceSettings({ enabled: open });
  const brandSetting =
    invoiceSettings.data?.settings.find((entry) => entry.brand === customerBrand) ?? null;

  function toggleNeedInvoice(checked: boolean): void {
    setNeedInvoice(checked);
    if (
      checked &&
      taxPoint.trim().length === 0 &&
      brandSetting !== null &&
      brandSetting.mode === 'cn-tax' &&
      brandSetting.default_tax_point_bps !== null
    ) {
      // Pre-fill the configured default 票点 as an editable percent.
      setTaxPoint(formatTaxPointBps(brandSetting.default_tax_point_bps).slice(0, -1));
    }
  }

  function finish(): void {
    toast.success(t('receipts.submit.toast.submitted'));
    onSubmitted?.();
    onOpenChange(false);
  }

  async function uploadEvidence(receiptId: string, evidence: File): Promise<void> {
    const buffer = await evidence.arrayBuffer();
    await upload.mutateAsync({
      receiptId,
      fileName: evidence.name,
      contentType: evidence.type.length > 0 ? evidence.type : 'application/octet-stream',
      dataBase64: bytesToBase64(new Uint8Array(buffer)),
    });
  }

  /**
   * Attachment-retry idempotency (#1098): a failed upload may have
   * landed server-side with only its response lost, so re-check the
   * receipt's attachment list before re-uploading — a retry can then
   * never duplicate the evidence. If the check itself fails, fall
   * through to the upload; it surfaces its own error.
   */
  async function retryEvidenceUpload(receiptId: string, evidence: File): Promise<void> {
    let alreadyUploaded = false;
    try {
      const existing = await listReceiptAttachments(receiptId, fetchImpl);
      alreadyUploaded = existing.attachments.some(
        (attachment) =>
          attachment.file_name === evidence.name && attachment.size_bytes === evidence.size,
      );
    } catch {
      // Best-effort check only — the upload attempt reports failures.
    }
    if (!alreadyUploaded) {
      await uploadEvidence(receiptId, evidence);
    }
  }

  function startUpload(run: () => Promise<void>): void {
    if (uploadInFlightRef.current) {
      return;
    }
    uploadInFlightRef.current = true;
    void run()
      .then(() => finish())
      .catch(() => {
        /* upload.error renders below */
      })
      .finally(() => {
        uploadInFlightRef.current = false;
      });
  }

  function handleSubmit(): void {
    if (busy) {
      return;
    }
    // Attachment-retry mode: the receipt already exists, only the
    // evidence upload is outstanding.
    if (pendingUpload !== null) {
      if (file === null) {
        // The operator cleared (or oversize-invalidated) the file after
        // the receipt landed — finishing here would silently drop the
        // evidence (#1098).
        setValidationError(t('receipts.submit.error.evidence_reselect'));
        return;
      }
      setValidationError(null);
      const retryReceiptId = pendingUpload.receiptId;
      startUpload(() => retryEvidenceUpload(retryReceiptId, file));
      return;
    }

    const parsedAmount = parseAmountInputToMinorUnits(amount);
    if (!parsedAmount.ok || parsedAmount.minorUnits <= 0) {
      setValidationError(t('receipts.submit.error.amount'));
      return;
    }
    let feeMinor = 0;
    if (fee.trim().length > 0) {
      const parsedFee = parseAmountInputToMinorUnits(fee);
      if (!parsedFee.ok || parsedFee.minorUnits < 0) {
        setValidationError(t('receipts.submit.error.fee'));
        return;
      }
      feeMinor = parsedFee.minorUnits;
    }
    if (receivedAt.length === 0) {
      setValidationError(t('receipts.submit.error.received_at'));
      return;
    }
    if (file !== null && isOversizeAttachment(file.size)) {
      setValidationError(t('receipts.submit.error.oversize', { mb: attachmentMaxMbLabel() }));
      return;
    }
    // Invoice fields (#1064): a cn-tax brand needs a parseable tax
    // point within the configured cap; commercial brands never send
    // one. Friendly client checks only — the platform re-validates
    // against brand_invoice_settings.
    let taxPointBps: number | null = null;
    if (needInvoice && brandSetting !== null && brandSetting.mode === 'cn-tax') {
      const parsedTaxPoint = parseTaxPointInputToBps(taxPoint);
      if (!parsedTaxPoint.ok) {
        setValidationError(t('receipts.submit.error.tax_point'));
        return;
      }
      if (parsedTaxPoint.bps > brandSetting.max_tax_point_bps) {
        setValidationError(
          t('receipts.submit.error.tax_point_cap', {
            cap: formatTaxPointBps(brandSetting.max_tax_point_bps),
          }),
        );
        return;
      }
      taxPointBps = parsedTaxPoint.bps;
    }
    setValidationError(null);

    const trimmedContractRef = contractRef.trim();
    const trimmedNote = note.trim();
    const trimmedInvoiceTitle = invoiceTitle.trim();
    const trimmedTaxNo = taxNo.trim();
    const collectTaxNo =
      needInvoice && brandSetting !== null && brandSetting.mode === 'cn-tax';
    submit.mutate(
      {
        orgId,
        clientRef,
        amountMinor: parsedAmount.minorUnits,
        currency,
        channel,
        actualFeeMinor: feeMinor,
        receivedAt,
        contractRef: trimmedContractRef.length > 0 ? trimmedContractRef : null,
        note: trimmedNote.length > 0 ? trimmedNote : null,
        needInvoice,
        taxPointBps,
        invoiceTitle: needInvoice && trimmedInvoiceTitle.length > 0 ? trimmedInvoiceTitle : null,
        taxNo: collectTaxNo && trimmedTaxNo.length > 0 ? trimmedTaxNo : null,
      },
      {
        onSuccess: (response) => {
          if (file === null) {
            finish();
            return;
          }
          setPendingUpload({ receiptId: response.receipt.id });
          startUpload(() => uploadEvidence(response.receipt.id, file));
        },
      },
    );
  }

  function handleFileChange(nextFile: File | null): void {
    if (nextFile !== null && isOversizeAttachment(nextFile.size)) {
      setValidationError(t('receipts.submit.error.oversize', { mb: attachmentMaxMbLabel() }));
      setFile(null);
      if (fileInputRef.current !== null) {
        fileInputRef.current.value = '';
      }
      return;
    }
    setValidationError(null);
    setFile(nextFile);
  }

  const submitError =
    submit.error !== null
      ? submit.error instanceof OpsApiError
        ? `${submit.error.code}: ${submit.error.message}`
        : submit.error.message
      : null;
  const uploadError =
    upload.error !== null
      ? t('receipts.submit.error.upload_failed', {
          detail:
            upload.error instanceof OpsApiError
              ? `${upload.error.code}: ${upload.error.message}`
              : upload.error.message,
        })
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) {
          onOpenChange(next);
        }
      }}
      title={t('receipts.submit.title')}
      description={t('receipts.submit.description')}
      closeLabel={t('common.close')}
      testId="receipt-submit-dialog"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="receipt-submit-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={busy}
            data-testid="receipt-submit-confirm"
          >
            {pendingUpload !== null
              ? t('receipts.submit.retry_upload')
              : t('receipts.submit.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1 rounded-lg bg-well px-3 py-2.5">
          <span className="text-label text-paper-low">{t('receipts.submit.customer')}</span>
          {contactCompany !== null ? (
            <span className="text-body font-medium text-paper" data-testid="receipt-submit-company">
              {contactCompany}
            </span>
          ) : null}
          <span className="text-body font-medium text-paper">{customerEmail}</span>
          <span className="font-mono text-micro text-paper-low">{orgId}</span>
          {contactQuery.isSuccess && contactCompany === null ? (
            <span
              className="text-micro text-warn"
              data-testid="receipt-submit-company-missing"
            >
              {t('receipts.submit.company_missing_hint')}
            </span>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t('receipts.submit.amount_label')}
            fieldId="receipt-submit-amount"
            required
            hint={t('receipts.submit.amount_hint')}
          >
            <Input
              id="receipt-submit-amount"
              value={amount}
              onChange={(event) => setAmount(event.currentTarget.value)}
              placeholder="0.00"
              inputMode="decimal"
              disabled={pendingUpload !== null}
              data-testid="receipt-submit-amount"
            />
          </FormField>
          <FormField label={t('receipts.submit.currency_label')} fieldId="receipt-submit-currency">
            <Select
              value={currency}
              onValueChange={(value) => {
                if (value === 'CNY' || value === 'USD') {
                  setCurrency(value);
                }
              }}
              options={RECEIPT_CURRENCIES.map((code) => ({ value: code, label: code }))}
              disabled={pendingUpload !== null}
              ariaLabel={t('receipts.submit.currency_label')}
              testId="receipt-submit-currency"
            />
          </FormField>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t('receipts.submit.channel_label')} fieldId="receipt-submit-channel">
            <Select
              value={channel}
              onValueChange={(value) => {
                const match = RECEIPT_CHANNELS.find((candidate) => candidate === value);
                if (match !== undefined) {
                  setChannel(match);
                }
              }}
              options={RECEIPT_CHANNELS.map((code) => ({
                value: code,
                label: t(RECEIPT_CHANNEL_LABEL_KEYS[code]),
              }))}
              disabled={pendingUpload !== null}
              ariaLabel={t('receipts.submit.channel_label')}
              testId="receipt-submit-channel"
            />
          </FormField>
          <FormField
            label={t('receipts.submit.fee_label')}
            fieldId="receipt-submit-fee"
            hint={t('receipts.submit.fee_hint')}
          >
            <Input
              id="receipt-submit-fee"
              value={fee}
              onChange={(event) => setFee(event.currentTarget.value)}
              placeholder="0.00"
              inputMode="decimal"
              disabled={pendingUpload !== null}
              data-testid="receipt-submit-fee"
            />
          </FormField>
        </div>

        <LocalDateTimeInput
          label={t('receipts.submit.received_at_label')}
          value={receivedAt}
          onChange={setReceivedAt}
          testId="receipt-submit-received-at"
        />

        <FormField
          label={t('receipts.submit.contract_ref_label')}
          fieldId="receipt-submit-contract-ref"
        >
          <Input
            id="receipt-submit-contract-ref"
            value={contractRef}
            onChange={(event) => setContractRef(event.currentTarget.value)}
            maxLength={200}
            disabled={pendingUpload !== null}
            data-testid="receipt-submit-contract-ref"
          />
        </FormField>

        {brandSetting !== null ? (
          <div
            className="flex flex-col gap-3 rounded-lg border border-line-soft bg-well/50 p-3"
            data-testid="receipt-submit-invoice-section"
          >
            <Checkbox
              checked={needInvoice}
              onCheckedChange={toggleNeedInvoice}
              disabled={pendingUpload !== null}
              label={t('receipts.submit.need_invoice_label')}
              testId="receipt-submit-need-invoice"
            />
            {needInvoice ? (
              <>
                <p className="m-0 text-micro text-paper-low">
                  {brandSetting.mode === 'cn-tax'
                    ? t('receipts.submit.invoice_mode_hint.cn_tax')
                    : t('receipts.submit.invoice_mode_hint.commercial')}
                </p>
                {brandSetting.mode === 'cn-tax' ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      label={t('receipts.submit.tax_point_label')}
                      fieldId="receipt-submit-tax-point"
                      required
                      hint={t('receipts.submit.tax_point_hint', {
                        cap: formatTaxPointBps(brandSetting.max_tax_point_bps),
                      })}
                    >
                      <Input
                        id="receipt-submit-tax-point"
                        value={taxPoint}
                        onChange={(event) => setTaxPoint(event.currentTarget.value)}
                        placeholder="6"
                        inputMode="decimal"
                        disabled={pendingUpload !== null}
                        data-testid="receipt-submit-tax-point"
                      />
                    </FormField>
                    <FormField
                      label={t('receipts.submit.tax_no_label')}
                      fieldId="receipt-submit-tax-no"
                    >
                      <Input
                        id="receipt-submit-tax-no"
                        value={taxNo}
                        onChange={(event) => setTaxNo(event.currentTarget.value)}
                        maxLength={64}
                        disabled={pendingUpload !== null}
                        data-testid="receipt-submit-tax-no"
                      />
                    </FormField>
                  </div>
                ) : null}
                <FormField
                  label={t('receipts.submit.invoice_title_label')}
                  fieldId="receipt-submit-invoice-title"
                >
                  <Input
                    id="receipt-submit-invoice-title"
                    value={invoiceTitle}
                    onChange={(event) => setInvoiceTitle(event.currentTarget.value)}
                    maxLength={200}
                    disabled={pendingUpload !== null}
                    data-testid="receipt-submit-invoice-title"
                  />
                </FormField>
              </>
            ) : null}
          </div>
        ) : null}

        <FormField label={t('receipts.submit.note_label')} fieldId="receipt-submit-note">
          <Textarea
            id="receipt-submit-note"
            value={note}
            onChange={(event) => setNote(event.currentTarget.value)}
            rows={2}
            disabled={pendingUpload !== null}
            data-testid="receipt-submit-note"
          />
        </FormField>

        <FormField
          label={t('receipts.submit.evidence_label')}
          fieldId="receipt-submit-evidence"
          hint={t('receipts.submit.evidence_hint', { mb: attachmentMaxMbLabel() })}
        >
          <input
            id="receipt-submit-evidence"
            ref={fileInputRef}
            type="file"
            onChange={(event) => handleFileChange(event.currentTarget.files?.[0] ?? null)}
            className="block w-full rounded-md border border-line-soft bg-canvas px-3 py-2 text-dense text-paper-mid file:mr-3 file:rounded file:border-0 file:bg-raised file:px-2.5 file:py-1 file:text-label file:text-paper"
            data-testid="receipt-submit-evidence"
          />
        </FormField>

        {validationError !== null ? (
          <p
            role="alert"
            data-testid="receipt-submit-validation-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {validationError}
          </p>
        ) : null}
        {pendingUpload !== null ? (
          <p
            role="alert"
            data-testid="receipt-submit-upload-pending"
            className="m-0 rounded-lg border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense text-warn"
          >
            {t('receipts.submit.upload_pending')}
          </p>
        ) : null}
        {submitError !== null ? (
          <p
            role="alert"
            data-testid="receipt-submit-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {submitError}
          </p>
        ) : null}
        {uploadError !== null ? (
          <p
            role="alert"
            data-testid="receipt-upload-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {uploadError}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
